'use client';

import * as Checkbox from '@radix-ui/react-checkbox';
import * as RadioGroup from '@radix-ui/react-radio-group';
import { DynamicIcon } from 'phosphor-icons/react';
import { useEffect, useState } from 'react';
import { c } from '../../../utils/tailwind-merge';
import {
  getHelpTextLabel,
  renderHelpText,
} from './VisibilityToggleGroupHelpText';

import type {
  VisibilityToggleGroupOption,
  VisibilityToggleGroupProps,
  VisibilityToggleGroupValue,
} from './VisibilityToggleGroup.types';

export const VisibilityToggleGroup = ({
  value: initialValue,
  variant = 'radio',
  showTitleIcon = true,
  disabled = false,
  microcopy,
  onValueChange,
  className = '',
}: VisibilityToggleGroupProps) => {
  const [value, setValue] = useState<VisibilityToggleGroupValue>(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const handleChange = (newValue: VisibilityToggleGroupValue) => {
    setValue(newValue);
    onValueChange(newValue);
  };
  const options: VisibilityToggleGroupOption[] = [
    {
      value: 'private',
      iconName: 'Lock',
      label: microcopy.private,
      helpText: microcopy.privateHelp,
    },
    {
      value: 'public',
      iconName: 'GlobeHemisphereWest',
      label: microcopy.public,
      helpText: microcopy.publicHelp,
    },
  ];

  const getItemClassName = (isSelected: boolean) =>
    c(
      'flex h-14 w-40 min-w-0 items-center rounded-lg pl-3 pr-1 py-2',
      'md:w-48 md:px-3 md:py-2',
      isSelected ? 'border-2 border-blue' : 'border border-grey-700',
      'disabled:cursor-not-allowed disabled:opacity-50',
      'transition-colors focus-visible:outline-none focus-visible:ring-2',
      'focus-visible:ring-blue focus-visible:ring-offset-2 focus-visible:ring-offset-grey-900'
    );

  const renderIndicator = (
    isSelected: boolean,
    variant: 'radio' | 'checkbox'
  ) => {
    if (variant === 'checkbox') {
      return (
        <span
          className={c(
            'flex h-4 w-4 items-center justify-center rounded transition-all',
            isSelected
              ? 'border-2 border-blue bg-blue'
              : 'border border-grey-500'
          )}
        >
          {isSelected && (
            <DynamicIcon
              iconName="Check"
              size={12}
              className="text-white"
              weight="bold"
              aria-hidden="true"
              focusable="false"
            />
          )}
        </span>
      );
    }

    return (
      <span
        className={c(
          'flex h-4 w-4 items-center justify-center rounded-full transition-all',
          isSelected
            ? 'border-4 border-blue bg-white'
            : 'border border-grey-500'
        )}
      />
    );
  };

  const renderItemContent = (
    option: VisibilityToggleGroupOption,
    isSelected: boolean
  ) => (
    <>
      <div className="hidden h-10 w-6 items-center justify-start md:flex">
        {renderIndicator(isSelected, variant)}
      </div>
      <div className="flex w-full min-w-0 flex-1 flex-col items-start justify-start overflow-hidden">
        <div className="flex w-full min-w-0 items-center gap-2">
          {showTitleIcon && (
            <DynamicIcon
              iconName={option.iconName}
              size={16}
              aria-hidden="true"
              focusable="false"
              className="text-grey-100"
              weight={isSelected ? 'fill' : 'regular'}
            />
          )}
          <span className="relative z-10 line-clamp-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-left text-base leading-6 tracking-tight text-grey-100">
            {option.label}
          </span>
        </div>
        {renderHelpText(option.helpText)}
      </div>
    </>
  );

  if (variant === 'checkbox') {
    return (
      <fieldset className={c('m-0 p-0', className)}>
        <legend className="sr-only">{microcopy.groupLabel}</legend>
        <div className="flex gap-3">
          {options.map((option) => {
            const isSelected = value === option.value;
            const helpTextLabel = getHelpTextLabel(option.helpText);
            return (
              <Checkbox.Root
                key={option.value}
                checked={isSelected}
                onCheckedChange={(checked) => {
                  if (checked) {
                    handleChange(option.value);
                  }
                }}
                disabled={disabled}
                aria-label={`${option.label}: ${helpTextLabel}`}
                data-test={`visibility-toggle-${option.value}`}
                className={getItemClassName(isSelected)}
              >
                {renderItemContent(option, isSelected)}
              </Checkbox.Root>
            );
          })}
        </div>
      </fieldset>
    );
  }

  return (
    <RadioGroup.Root
      value={value}
      onValueChange={handleChange}
      disabled={disabled}
      aria-label={microcopy.groupLabel}
      className={c('flex gap-3', className)}
    >
      {options.map((option) => {
        const isSelected = value === option.value;
        const helpTextLabel = getHelpTextLabel(option.helpText);
        return (
          <RadioGroup.Item
            key={option.value}
            value={option.value}
            aria-label={`${option.label}: ${helpTextLabel}`}
            data-test={`visibility-toggle-${option.value}`}
            className={getItemClassName(isSelected)}
          >
            {renderItemContent(option, isSelected)}
          </RadioGroup.Item>
        );
      })}
    </RadioGroup.Root>
  );
};
